import { useState } from 'react';
import { MainLayout } from '@templates/index';
import { Card, Button, Badge } from '@atoms/index';
import { useAuthStore } from '@store/authStore';
import { supportService, SupportTicket, SupportTicketStatus, SupportTicketPriority, CreateTicketDto } from '@services/supportService';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { cn } from '@/utils/cn';

const STATUS_LABELS: Record<SupportTicketStatus, string> = {
  OPEN: 'Açık',
  IN_PROGRESS: 'İşlemde',
  RESOLVED: 'Çözüldü',
  CLOSED: 'Kapatıldı',
};

const PRIORITY_LABELS: Record<SupportTicketPriority, string> = {
  LOW: 'Düşük',
  MEDIUM: 'Orta',
  HIGH: 'Yüksek',
  URGENT: 'Acil',
};

const STATUS_BADGE_VARIANT: Record<SupportTicketStatus, 'default' | 'info' | 'warning' | 'success'> = {
  OPEN: 'info',
  IN_PROGRESS: 'warning',
  RESOLVED: 'success',
  CLOSED: 'default',
};

const PRIORITY_COLORS: Record<SupportTicketPriority, string> = {
  LOW: 'text-gray-500',
  MEDIUM: 'text-orange-500',
  HIGH: 'text-amber-600',
  URGENT: 'text-red-600',
};

function TicketCard({ ticket, onClose }: { ticket: SupportTicket; onClose: (id: string) => void }) {
  const [expanded, setExpanded] = useState(false);
  const isClosed = ticket.status === 'CLOSED' || ticket.status === 'RESOLVED';

  return (
    <Card className="hover:shadow-md transition-shadow">
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap mb-1">
            <h3 className="font-semibold text-gray-900 text-sm truncate">{ticket.subject}</h3>
            <Badge variant={STATUS_BADGE_VARIANT[ticket.status]}>{STATUS_LABELS[ticket.status]}</Badge>
          </div>
          <p
            className={cn(
              'text-sm text-gray-600 leading-relaxed',
              !expanded && 'line-clamp-2'
            )}
          >
            {ticket.description}
          </p>
          {ticket.description.length > 120 && (
            <button
              onClick={() => setExpanded((v) => !v)}
              className="text-xs text-[#E63946] mt-1 hover:underline"
            >
              {expanded ? 'Daha az göster' : 'Devamını oku'}
            </button>
          )}
          {ticket.resolutionNotes && (
            <div className="mt-3 p-3 bg-green-50 border border-green-200 rounded-lg">
              <p className="text-xs font-semibold text-green-700 mb-1">Çözüm Notu</p>
              <p className="text-sm text-green-800">{ticket.resolutionNotes}</p>
            </div>
          )}
        </div>
        <div className="flex flex-col items-end gap-2 shrink-0">
          <span className={cn('text-xs font-medium', PRIORITY_COLORS[ticket.priority])}>
            {PRIORITY_LABELS[ticket.priority]}
          </span>
          <span className="text-xs text-gray-400">
            {new Date(ticket.createdAt).toLocaleDateString('tr-TR')}
          </span>
          {!isClosed && (
            <Button size="sm" variant="default" onClick={() => onClose(ticket.id)}>
              Kapat
            </Button>
          )}
        </div>
      </div>
    </Card>
  );
}

function CreateTicketModal({ onClose, onSuccess }: { onClose: () => void; onSuccess: () => void }) {
  const [form, setForm] = useState<CreateTicketDto>({
    subject: '',
    description: '',
    priority: 'MEDIUM',
  });
  const [errors, setErrors] = useState<Partial<Record<keyof CreateTicketDto, string>>>({});

  const mutation = useMutation({
    mutationFn: (dto: CreateTicketDto) => supportService.createTicket(dto),
    onSuccess: () => {
      onSuccess();
      onClose();
    },
  });

  const validate = (): boolean => {
    const newErrors: typeof errors = {};
    if (!form.subject.trim() || form.subject.trim().length < 5) {
      newErrors.subject = 'Konu en az 5 karakter olmalıdır';
    }
    if (!form.description.trim() || form.description.trim().length < 10) {
      newErrors.description = 'Açıklama en az 10 karakter olmalıdır';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;
    mutation.mutate(form);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <h2 className="text-lg font-bold text-gray-900">Destek Talebi Oluştur</h2>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100 text-gray-500 transition-colors"
          >
            ✕
          </button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">Konu</label>
            <input
              type="text"
              value={form.subject}
              onChange={(e) => setForm((f) => ({ ...f, subject: e.target.value }))}
              maxLength={255}
              placeholder="Örn: Siparişim gelmedi"
              className={cn(
                'w-full px-3 py-2.5 border rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#E63946] focus:border-transparent',
                errors.subject ? 'border-red-400' : 'border-gray-300'
              )}
            />
            {errors.subject && <p className="text-xs text-red-500 mt-1">{errors.subject}</p>}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">Açıklama</label>
            <textarea
              value={form.description}
              onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
              maxLength={4000}
              rows={4}
              placeholder="Yaşadığınız sorunu detaylı olarak açıklayın..."
              className={cn(
                'w-full px-3 py-2.5 border rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#E63946] focus:border-transparent resize-none',
                errors.description ? 'border-red-400' : 'border-gray-300'
              )}
            />
            {errors.description && <p className="text-xs text-red-500 mt-1">{errors.description}</p>}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">Öncelik</label>
            <div className="flex gap-2 flex-wrap">
              {(['LOW', 'MEDIUM', 'HIGH', 'URGENT'] as SupportTicketPriority[]).map((p) => (
                <button
                  key={p}
                  type="button"
                  onClick={() => setForm((f) => ({ ...f, priority: p }))}
                  className={cn(
                    'px-3 py-1.5 rounded-lg text-sm font-medium border transition-all',
                    form.priority === p
                      ? 'bg-[#E63946] text-white border-[#E63946]'
                      : 'bg-white text-gray-600 border-gray-300 hover:border-gray-400'
                  )}
                >
                  {PRIORITY_LABELS[p]}
                </button>
              ))}
            </div>
          </div>

          {mutation.isError && (
            <p className="text-sm text-red-500">Talep oluşturulamadı. Lütfen tekrar deneyin.</p>
          )}

          <div className="flex gap-3 pt-2">
            <Button type="button" variant="default" className="flex-1" onClick={onClose}>
              İptal
            </Button>
            <Button
              type="submit"
              variant="primary"
              className="flex-1"
              disabled={mutation.isPending}
            >
              {mutation.isPending ? 'Gönderiliyor...' : 'Talebi Gönder'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}

const SupportPage = () => {
  const { isAuthenticated } = useAuthStore();
  const queryClient = useQueryClient();
  const [showModal, setShowModal] = useState(false);
  const [activeFilter, setActiveFilter] = useState<SupportTicketStatus | 'ALL'>('ALL');

  const { data, isLoading, isError } = useQuery({
    queryKey: ['my-tickets', activeFilter],
    queryFn: () =>
      supportService.getMyTickets({
        status: activeFilter === 'ALL' ? undefined : activeFilter,
        limit: 50,
      }),
    enabled: isAuthenticated,
  });

  const closeMutation = useMutation({
    mutationFn: (ticketId: string) => supportService.closeMyTicket(ticketId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['my-tickets'] }),
  });

  const filters: Array<{ label: string; value: SupportTicketStatus | 'ALL' }> = [
    { label: 'Tümü', value: 'ALL' },
    { label: 'Açık', value: 'OPEN' },
    { label: 'İşlemde', value: 'IN_PROGRESS' },
    { label: 'Çözüldü', value: 'RESOLVED' },
    { label: 'Kapatıldı', value: 'CLOSED' },
  ];

  return (
    <MainLayout>
      <div className="max-w-3xl mx-auto py-8 px-4">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Destek Merkezi</h1>
            <p className="text-sm text-gray-500 mt-0.5">Taleplerinizi takip edin ve yeni talep oluşturun</p>
          </div>
          {isAuthenticated && (
            <Button variant="primary" onClick={() => setShowModal(true)}>
              + Yeni Talep
            </Button>
          )}
        </div>

        {!isAuthenticated ? (
          <Card>
            <div className="text-center py-12">
              <div className="w-16 h-16 bg-red-50 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-3xl">🎧</span>
              </div>
              <h2 className="text-lg font-semibold text-gray-900 mb-2">Destek Talebi Oluşturun</h2>
              <p className="text-gray-500 text-sm mb-6 max-w-sm mx-auto">
                Destek talebi oluşturmak ve mevcut taleplerinizi takip etmek için giriş yapmanız gerekmektedir.
              </p>
              <Button variant="primary" onClick={() => (window.location.href = '/login')}>
                Giriş Yap
              </Button>
            </div>
          </Card>
        ) : (
          <>
            <div className="flex gap-2 mb-5 flex-wrap">
              {filters.map((f) => (
                <button
                  key={f.value}
                  onClick={() => setActiveFilter(f.value)}
                  className={cn(
                    'px-3 py-1.5 rounded-full text-sm font-medium transition-all',
                    activeFilter === f.value
                      ? 'bg-[#E63946] text-white'
                      : 'bg-white text-gray-600 border border-gray-200 hover:border-gray-300'
                  )}
                >
                  {f.label}
                </button>
              ))}
            </div>

            {isLoading && (
              <div className="flex justify-center py-16">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#E63946]" />
              </div>
            )}

            {isError && (
              <Card>
                <p className="text-center text-red-500 py-8">Talepler yüklenemedi. Lütfen sayfayı yenileyin.</p>
              </Card>
            )}

            {!isLoading && !isError && data?.tickets.length === 0 && (
              <Card>
                <div className="text-center py-12">
                  <div className="w-16 h-16 bg-orange-50 rounded-full flex items-center justify-center mx-auto mb-4">
                    <span className="text-3xl">📭</span>
                  </div>
                  <h2 className="text-lg font-semibold text-gray-900 mb-2">Henüz talep yok</h2>
                  <p className="text-gray-500 text-sm mb-6">
                    {activeFilter === 'ALL'
                      ? 'Bir sorunla karşılaştığınızda destek talebi oluşturabilirsiniz.'
                      : `${STATUS_LABELS[activeFilter as SupportTicketStatus]} durumunda talep bulunmuyor.`}
                  </p>
                  <Button variant="primary" onClick={() => setShowModal(true)}>
                    Destek Talebi Oluştur
                  </Button>
                </div>
              </Card>
            )}

            {!isLoading && !isError && (data?.tickets.length ?? 0) > 0 && (
              <div className="space-y-3">
                {data!.tickets.map((ticket) => (
                  <TicketCard
                    key={ticket.id}
                    ticket={ticket}
                    onClose={(id) => closeMutation.mutate(id)}
                  />
                ))}
              </div>
            )}
          </>
        )}
      </div>

      {showModal && (
        <CreateTicketModal
          onClose={() => setShowModal(false)}
          onSuccess={() => queryClient.invalidateQueries({ queryKey: ['my-tickets'] })}
        />
      )}
    </MainLayout>
  );
};

export default SupportPage;
